import React, { useMemo, useState, useEffect, useRef } from "react";
import ReusableTable from "../components/Table/ReusableTable";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { ArrowBigLeft, Plus, Download, Search, Calendar as CalendarIcon, XCircle, Eye } from "lucide-react";
import moment from "moment";
import Button from "../components/Button/Button";
import CustomDialog from "../components/dialog/CustomDialog";
import { post } from "../utils/api";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const formatDate = (date) => {
  if (!date) return "";
  return date.toLocaleDateString("en-US");
};

const getLastThreeMonths = () => {
  const now = new Date();
  const options = { month: "long" };
  return [0, 1, 2].map((offset) => {
    const date = new Date(now.getFullYear(), now.getMonth() - offset);
    return date.toLocaleString("en-US", options);
  });
};

const TransactionPage = () => {
  const [allData, setAllData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);

  const fromWrapperRef = useRef(null);
  const toWrapperRef = useRef(null);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [month, setMonth] = useState("");
  const [uploadType, setUploadType] = useState("Transactions"); // default
  const [file, setFile] = useState(null);

  const months = getLastThreeMonths();
  const uploadOptions = ["Transactions"]; // default + only one option

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "list" | "details"

  const formatUploadDateTime = (dateString) => {
    if (!dateString) return "";
    return moment(dateString, "DD-MM-YYYY hh:mm:ss A").format(
      "DD-MM-YY HH:mm:ss"
    );
  };

  const formatTransactionDate = (dateStr) => {
    if (!dateStr) return "";
    return moment(dateStr).format("DD-MM-YYYY");
  };

  const formatMonthYear = (dateStr) => {
    if (!dateStr) return "";
    return moment(dateStr).format("MM-YYYY");
  };

  // Fetch transactions from API
  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await post("/transactionupload/fetchTransactions", {});
      if (response.data.Success && Array.isArray(response.data.Data)) {
        const formatted = response.data.Data.map((item) => ({
          transaction_id: item.id,
          uploadDateTime: formatUploadDateTime(item.uploded_date_time),
          month: item.month,
          uploadedBy: item.uploded_by,
          errorCount: item.error_count,
          status: item.status_desc,
          fileName: `${item.month_year}.csv`,
          action: "View",
        }));
        setAllData(formatted);
        setTableData(formatted);
      } else {
        setAllData([]);
        setTableData([]);
        toast.info("No transaction records found.");
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setAllData([]);
      setTableData([]);
      toast.error("Failed to fetch transactions.");
    }
  };

  // Filter data
  useEffect(() => {
    const filtered = allData.filter((item) => {
      const matchesSearch =
        item.fileName.toLowerCase().includes(searchText.toLowerCase()) ||
        item.uploadedBy.toLowerCase().includes(searchText.toLowerCase());

      // Convert uploadDateTime string to Date object
      const uploadDate = new Date(
        item.uploadDateTime.replace(
          /(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2}) (AM|PM)/,
          (match, dd, mm, yyyy, hh, mi, ss, meridian) => {
            let hours = parseInt(hh, 10);
            if (meridian === "PM" && hours < 12) hours += 12;
            if (meridian === "AM" && hours === 12) hours = 0;
            return `${yyyy}-${mm}-${dd}T${hours
              .toString()
              .padStart(2, "0")}:${mi}:${ss}`;
          }
        )
      );

      // Normalize date ranges
      const from = fromDate ? new Date(fromDate) : null;
      if (from) from.setHours(0, 0, 0, 0);

      const to = toDate ? new Date(toDate) : null;
      if (to) to.setHours(23, 59, 59, 999);

      const matchesFromDate = from ? uploadDate >= from : true;
      const matchesToDate = to ? uploadDate <= to : true;

      return matchesSearch && matchesFromDate && matchesToDate;
    });

    setTableData(filtered);
  }, [searchText, fromDate, toDate, allData]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        fromWrapperRef.current &&
        !fromWrapperRef.current.contains(event.target)
      ) {
        setShowFromCalendar(false);
      }
      if (
        toWrapperRef.current &&
        !toWrapperRef.current.contains(event.target)
      ) {
        setShowToCalendar(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const columns = useMemo(
    () => [
      { Header: "Upload Date Time", accessor: "uploadDateTime", size: "180px" },
      { Header: "Month", accessor: "month", size: "120px" },
      { Header: "Uploaded By", accessor: "uploadedBy", size: "150px" },
      { Header: "Error Count", accessor: "errorCount", size: "120px" },
      { Header: "Status", accessor: "status", size: "180px" },
      {
        Header: "ACTION",
        accessor: "action",
        size: "120px",
        Cell: ({ row }) => (
          <button
            className="bg-[#F1F4F9] border border-[#E2E8F0] text-gray-800 px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 hover:bg-gray-200 text-[11px] font-semibold"
            onClick={() => handleView(row.original)}
          >
            <Eye size={14} />
            View
          </button>
        ),
      },
    ],
    []
  );

  // Export to Excel function - sorts by transaction_id in ascending order
  const handleExportToExcel = () => {
    const dataToExport = searchText || fromDate || toDate ? tableData : allData;

    if (!dataToExport || dataToExport.length === 0) {
      toast.info("No data to export");
      return;
    }

    // Sort data by transaction_id in ascending order
    const sortedData = [...dataToExport].sort((a, b) => {
      const idA = Number(a.transaction_id);
      const idB = Number(b.transaction_id);
      return idA - idB;
    });

    // Define CSV headers
    const headers = [
      "Transaction ID",
      "Upload Date Time",
      "Month",
      "Uploaded By",
      "Error Count",
      "Status",
      "File Name",
    ];

    // Prepare CSV rows
    const csvRows = sortedData.map((row) => [
      row.transaction_id,
      row.uploadDateTime,
      row.month,
      row.uploadedBy,
      row.errorCount,
      row.status,
      row.fileName,
    ]);

    // Convert to CSV format
    const csvContent = [
      headers.join(","),
      ...csvRows.map((row) =>
        row
          .map((field) => {
            if (field === null || field === undefined) return "";
            const str = field.toString();
            if (str.includes(",") || str.includes('"') || str.includes("\n")) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          })
          .join(",")
      ),
    ].join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `transactions_export_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Data exported to CSV successfully!");
  };

  const downloadCSV = (data, fileName = "export.csv") => {
    if (!data || data.length === 0) {
      toast.info("No data to download");
      return;
    }

    // Sort data by 'id' in ascending order
    const sortedData = [...data].sort((a, b) => {
      const idA = Number(a.id);
      const idB = Number(b.id);
      return idA - idB;
    });

    // Define the order of columns with new fund fields
    const preferredOrder = [
      "transaction_date",
      "process_month",
      "client_name",
      "pan_number",
      "folio_no",
      "distributor_arn_code",
      "distributor_pan_number",
      "product_code",
      "fund_code",
      "fund_name", // New fields
      "capital_commitment",
      "management_fee_sharing_per",
      "management_fee_amount",
      "performance_fee_sharing_per",
      "performance_fee_amount",
      "placement_fee_per",
      "placement_fee_amount",
      "upfront_fee_per",
      "upfront_fee_amount",
      "trail_per",
      "total_trail_amount",
      "trail_start_month",
      "trail_end_month",
      "monthly_trail_amount",
      "total_commission",
      "gst",
      "tds",
      "net_commission_payable",
      "uploaded_remarks",
      "check_remarks",
      "status_id",
    ];

    // Get all headers, but prioritize the preferred order
    const allHeaders = Object.keys(sortedData[0]);
    const headers = [
      ...preferredOrder.filter((header) => allHeaders.includes(header)),
      ...allHeaders.filter((header) => !preferredOrder.includes(header)),
    ];

    const csvRows = sortedData.map((row) =>
      headers
        .map((header) => {
          let val =
            row[header] === null || row[header] === undefined
              ? ""
              : row[header];
          val = val.toString().replace(/"/g, '""');
          if (val.search(/("|,|\n)/g) >= 0) val = `"${val}"`;
          return val;
        })
        .join(",")
    );

    const csvContent = [headers.join(","), ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName.replace(/\s+/g, "_");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleView = async (rowData) => {
    try {
      const response = await post("/transactionupload/fetchTransactionsById", {
        transaction_id: rowData.transaction_id,
      });

      if (response.data?.Success) {
        const { transactions_details } = response.data.Data[0];

        if (!transactions_details || transactions_details.length === 0) {
          toast.warn("No transaction details found.");
          return;
        }

        const formattedDetails = transactions_details.map((tx) => ({
          ...tx,
          transaction_date: formatTransactionDate(tx.transaction_date),
          trail_start_month: formatMonthYear(tx.trail_start_month),
          trail_end_month: formatMonthYear(tx.trail_end_month),
        }));

        setSelectedTransaction({
          ...rowData,
          details: formattedDetails,
        });

        setViewMode("details");
      } else {
        toast.error(
          response.data.Message || "Failed to fetch transaction details."
        );
      }
    } catch (error) {
      console.error("View error:", error);
      toast.error("Something went wrong while fetching transaction details.");
    }
  };

  const handleBack = () => {
    setSelectedTransaction(null);
    setViewMode("list");
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file || !month || !uploadType) {
      toast.error("Please select a file, month, and upload type.");
      return;
    }

    const reader = new FileReader();

    reader.onload = async (e) => {
      const contents = e.target.result;
      const lines = contents
        .split(/\r\n|\n/)
        .filter((line) => line.trim() !== "");

      if (lines.length <= 1) {
        toast.error("The uploaded file is empty");
        setDialogVisible(false);
        return;
      }

      try {
        const formData = new FormData();
        const year = new Date().getFullYear();
        const userId = localStorage.getItem("userId") || 1;

        formData.append("transactionFile", file);
        formData.append("month", JSON.stringify(month));
        formData.append("year", JSON.stringify(year));
        formData.append("upload_type", JSON.stringify(uploadType));
        formData.append("userId", JSON.stringify(userId));

        const response = await post(
          "/transactionupload/saveTransactions",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const result = response.data;

        // Close dialog and reset state FIRST, before any further state updates
        // This prevents PrimeReact Dialog's internal focus-trap ref (toggle) from
        // being accessed on an already-unmounted element.
        setDialogVisible(false);
        setFile(null);
        setMonth("");

        if (result.Success) {
          toast.success(result.Message || "Upload successful!");
          fetchTransactions();
        } else {
          if (Array.isArray(result.Data) && result.Data.length > 0) {
            downloadFailedRowsCSV(result.Data);
            toast.error("Validation for some rows failed.");
          } else {
            toast.error(result.Message || "Upload failed.");
          }
        }
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("An error occurred while uploading. Please try again.");
        setDialogVisible(false);
        setFile(null);
        setMonth("");
      }
    };

    reader.readAsText(file);
  };

  // Helper function to download CSV from failed rows
  const downloadFailedRowsCSV = (failedRows) => {
    if (!failedRows || failedRows.length === 0) {
      toast.info("No failed rows to download");
      return;
    }

    const headers = Object.keys(failedRows[0]);

    const csvRows = failedRows.map((row) =>
      headers
        .map((header) => {
          let val =
            row[header] === null || row[header] === undefined
              ? ""
              : row[header];
          val = val.toString().replace(/"/g, '""');

          if (val.search(/("|,|\n)/g) >= 0) val = `"${val}"`;
          return val;
        })
        .join(",")
    );

    const csvContent = [headers.join(","), ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Failed_Transactions.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSampleData = () => {
    const headers = [
      "Transaction Date",
      "Process Month",
      "Client Name",
      "PAN",
      "Folio No.",
      "Distributor Code (APRN)",
      "Distributor PAN",
      "Product Code",
      "Fund Code", // New column added here
      "Capital Commitment",
      "Management Fee Sharing %",
      "Management Fee Amount",
      "Performance Fee Sharing %",
      "Performance Fee Amount",
      "Placement Fee %",
      "Placement Fee Amount",
      "Upfront Fee %",
      "Upfront Fee Amount",
      "Trail %",
      "Total Trail Amount",
      "Trail Start Month",
      "Trail End Month",
      "Monthly Trail Amount",
      "Total Commission",
      "GST",
      "TDS",
      "Net Commission Payable",
      "Remarks",
    ];

    const csvContent = headers.join(",") + "\n";

    // Create Blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Sample_Transaction.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Sample CSV downloaded successfully!");
  };

  function formatKeyToHeader(key) {
    return key.replace(/_/g, " ");
  }

  return (
    <div className="w-full max-w-screen-xl mx-auto p-4 space-y-6">
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar theme="colored" />
      {viewMode === "details" ? (
        <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 space-y-4">
          <div className="flex justify-between items-center bg-gray-50/50 p-4 rounded-xl">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-bold text-xs uppercase tracking-widest transition-all"
            >
              <ArrowBigLeft size={18} /> Back to Transactions
            </button>
            <button
              onClick={() =>
                downloadCSV(
                  selectedTransaction.details,
                  `${selectedTransaction.fileName || "transaction_details"}`
                )
              }
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-bold text-xs uppercase tracking-widest shadow-lg shadow-indigo-100 transition-all flex items-center gap-2"
            >
              <Download size={14} /> Download To Excel
            </button>
          </div>

          <h2 className="text-xl font-bold text-gray-800 tracking-tight">Transaction Details</h2>

          <div className="custom-registry-table">
            <style>{`
              .custom-registry-table th {
                color: #4f46e5;
                font-size: 11px;
                font-weight: 800;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                padding: 16px 12px;
                border-bottom: 1px solid #f3f4f6;
              }
              .custom-registry-table td {
                padding: 16px 12px;
                vertical-align: middle;
              }
            `}</style>
            {selectedTransaction.details.length > 0 ? (
              <ReusableTable
                columns={Object.keys(selectedTransaction.details[0])
                  .filter(
                    (key) =>
                      ![
                        // "id",
                        // "transaction_id",
                        "is_active",
                        "created_by",
                        "created_date",
                        "modified_by",
                        "modified_date",
                        "invoice_number",
                        "id",
                        "transaction_id",
                      ].includes(key)
                  )
                  .map((key) => {
                    // Logic remains same, just styling encapsulated
                    if (key === "utr_number") {
                      return [
                        {
                          Header: "UTR Number",
                          accessor: "utr_number",
                          size: "150px",
                          Cell: ({ value }) => <div className="text-[11px] font-medium text-gray-700">{value}</div>
                        },
                        {
                          Header: "Invoice Number",
                          accessor: "invoice_number",
                          size: "150px",
                          Cell: ({ value }) => <div className="text-[11px] font-medium text-gray-700">{value}</div>
                        }
                      ];
                    }
                    return {
                      Header: formatKeyToHeader(key).toUpperCase(),
                      accessor: key,
                      size: "150px",
                      Cell: ({ value }) => (
                        <div className="text-[11px] font-medium text-gray-600" title={value}>
                          {value}
                        </div>
                      ),
                    };
                  })
                  .flat()}
                data={selectedTransaction.details}
              />
            ) : (
              <div className="text-center py-20 text-gray-400 font-medium text-sm">
                No transaction details available.
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-xl font-bold text-gray-800 tracking-tight">Transactions</h1>
              <p className="text-[11px] text-gray-400 font-medium tracking-wide">View and manage all transaction records</p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setDialogVisible(true)}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-bold text-xs uppercase tracking-widest shadow-lg shadow-indigo-100 transition-all active:scale-95"
              >
                <Plus size={16} /> Add Transaction
              </button>
              <button
                onClick={handleExportToExcel}
                className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 px-6 py-2.5 rounded-lg font-bold text-xs uppercase tracking-widest shadow-sm transition-all active:scale-95"
              >
                <Download size={16} className="text-indigo-600" /> Export to Excel
              </button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Search */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-extrabold text-gray-400 uppercase tracking-widest ml-1">Search Transactions</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                  </div>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search by file name or uploaded by"
                    className="w-full h-[46px] pl-10 pr-4 border border-gray-100 rounded-xl font-medium text-sm bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-indigo-50/50 focus:border-indigo-200 outline-none transition-all placeholder:text-gray-300"
                  />
                </div>
              </div>

              {/* From Date */}
              <div ref={fromWrapperRef} className="space-y-1.5 relative">
                <label className="text-[11px] font-extrabold text-gray-400 uppercase tracking-widest ml-1">Upload From Date</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <CalendarIcon className="h-4 w-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                  </div>
                  <input
                    type="text"
                    readOnly
                    value={formatDate(fromDate)}
                    placeholder="Select a date"
                    onClick={() => {
                      setShowFromCalendar((prev) => !prev);
                      setShowToCalendar(false);
                    }}
                    className="w-full h-[46px] pl-10 pr-10 border border-gray-100 rounded-xl font-medium text-sm bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-indigo-50/50 outline-none transition-all cursor-pointer"
                  />
                  {fromDate && (
                    <button
                      onClick={() => setFromDate(null)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-400 hover:text-rose-600 transition-colors"
                    >
                      <XCircle size={16} />
                    </button>
                  )}
                </div>
                {showFromCalendar && (
                  <div className="absolute z-50 top-full left-0 mt-2 bg-white shadow-2xl rounded-xl border border-gray-100 overflow-hidden animation-fade-in">
                    <Calendar
                      onChange={(date) => {
                        setFromDate(date);
                        setShowFromCalendar(false);
                      }}
                      value={fromDate}
                      maxDate={toDate || new Date()}
                    />
                  </div>
                )}
              </div>

              {/* To Date */}
              <div ref={toWrapperRef} className="space-y-1.5 relative">
                <label className="text-[11px] font-extrabold text-gray-400 uppercase tracking-widest ml-1">Upload To Date</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <CalendarIcon className="h-4 w-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                  </div>
                  <input
                    type="text"
                    readOnly
                    value={formatDate(toDate)}
                    placeholder="Select a date"
                    onClick={() => {
                      setShowToCalendar((prev) => !prev);
                      setShowFromCalendar(false);
                    }}
                    className="w-full h-[46px] pl-10 pr-10 border border-gray-100 rounded-xl font-medium text-sm bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-indigo-50/50 outline-none transition-all cursor-pointer"
                  />
                  {toDate && (
                    <button
                      onClick={() => setToDate(null)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-400 hover:text-rose-600 transition-colors"
                    >
                      <XCircle size={16} />
                    </button>
                  )}
                </div>
                {showToCalendar && (
                  <div className="absolute z-50 top-full left-0 mt-2 bg-white shadow-2xl rounded-xl border border-gray-100 overflow-hidden animation-fade-in">
                    <Calendar
                      onChange={(date) => {
                        setToDate(date);
                        setShowToCalendar(false);
                      }}
                      value={toDate}
                      minDate={fromDate}
                      maxDate={new Date()}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table Container */}
          <div className="custom-registry-table">
            <style>{`
                .custom-registry-table th {
                  color: #4f46e5;
                  font-size: 11px;
                  font-weight: 800;
                  text-transform: uppercase;
                  letter-spacing: 0.05em;
                  padding: 16px 12px;
                  border-bottom: 1px solid #f3f4f6;
                }
                .custom-registry-table td {
                  padding: 16px 12px;
                  vertical-align: middle;
                  font-size: 12px;
                  color: #374151;
                }
              `}</style>
            {tableData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-32 text-gray-400 font-medium text-sm">
                {searchText || fromDate || toDate
                  ? "No matching transactions found for your search criteria."
                  : "No transaction records found."}
              </div>
            ) : (
              <ReusableTable columns={columns} data={tableData} />
            )}
          </div>

          {/* Upload Dialog */}
          <CustomDialog
            visible={dialogVisible}
            onHide={() => setDialogVisible(false)}
            header="Upload Transactions"
            width="40vw"
            closable={true}
          >
            <div className="space-y-4">
              {/* Month Field */}
              <div>
                <label className="block text-sm font-medium mb-1">Month</label>
                <select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 w-full"
                >
                  <option value="">Select Month</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {/* Upload Type */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Upload Type
                </label>
                <select
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 w-full"
                >
                  {uploadOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              {/* File Input */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Choose File
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full"
                  accept=".csv,.xlsx"
                />
                {file && (
                  <p className="mt-1 text-sm text-gray-600">
                    Selected file: {file.name}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-4">
                <Button
                  onClick={handleUpload}
                  disabled={!file || !month || !uploadType}
                  variant="default"
                >
                  Upload
                </Button>
                <Button onClick={handleSampleData} variant="outline">
                  Sample Data
                </Button>
              </div>
            </div>
          </CustomDialog>
          <ToastContainer position="top-right" autoClose={3000} />
        </>
      )}
    </div>
  );
};

export default TransactionPage;
